import _ from 'lodash';
import { Iterable } from 'immutable';
import { timeToInt } from './dates';

/**
 * Counts consecutive objects in array with the same fieldName value.
 * Returns array of same size, where the first entry for each sequence holds the count for that sequence,
 * and succeeding values are 0. Look at tests for examples.

 Example:

 let collection = [
 {weekday: "Tuesday"},
 {weekday: "Tuesday"},
 {weekday: "Tuesday"},
 {weekday: "Wednesday"},
 {weekday: "Wednesday"},
 {weekday: "Wednesday"},
 {weekday: "Wednesday"},
 {weekday: "Friday"},
 {weekday: "Friday"},
 {weekday: "Saturday"},
 {weekday: "Sunday"},
 {weekday: "Sunday"},
 {weekday: "Sunday"},
 {weekday: "Sunday"},
 {weekday: "Sunday"},
 {weekday: "Sunday"},
 {weekday: "Monday"},
 ];

 countConsecutives(collection, 'weekday') // Returns: [3, 0, 0, 4, 0, 0, 0, 2, 0, 1, 6, 0, 0, 0, 0, 1];;

 * @param collection an array of objects
 * @param comparator either a string denoting the fieldName or a function that is invoked for each object
 */
export function countConsecutives(collection, comparator) {
  if (collection == undefined) {
    throw new Error("Collection isn't specified");
  }

  if (comparator == undefined) {
    throw new Error("Comparator isn't specified");
  }

  if (typeof (comparator) !== 'string' && typeof (comparator) !== 'function') {
    throw new Error('Expected comparator to be either a string denoting field name or a function');
  }

  let transform;
  if (typeof (comparator) == 'string') {
    if (collection.length > 0 && !(comparator in collection[0])) {
      throw new Error("No such field in collection, maybe you've misspelled it?");
    }

    transform = function (item) {
      return item[comparator];
    };
  } else {
    transform = comparator;
  }
  const result = new Array(collection.length).fill(0);
  let writeIndex = 0;
  let previousValue;
  collection.forEach((item, readIndex) => {
    if (transform(item) == previousValue) {
      result[writeIndex]++;
    } else {
      previousValue = transform(item);
      writeIndex = readIndex;
      result[writeIndex] = 1;
    }
  });
  return result;
}


export function sortTimeplanUnits(t1, t2) {
  // Convert to regular objects, do comparison in with plain Javascript
  t1 = Iterable.isIterable(t1) ? t1.toJS() : t1;
  t2 = Iterable.isIterable(t2) ? t2.toJS() : t2;

  const dateDiff = new Date(t1.date) - new Date(t2.date);
  if (dateDiff != 0) {
    return dateDiff;
  }

  if ('resourceName' in t1 && 'resourceName' in t2) {
    if (t1.resourceName < t2.resourceName) {
      return -1;
    }

    if (t1.resourceName > t2.resourceName) {
      return 1;
    }
  }

  try {
    const fromDiff = timeToInt(t1.from) - timeToInt(t2.from);
    if (fromDiff != 0) {
      return fromDiff;
    }

    const toDiff = timeToInt(t1.to) - timeToInt(t2.to);
    if (toDiff != 0) {
      return toDiff;
    }
  } catch (error) {
    // Do nothing
  }

  return 0;
}

/**
 * Gets the number equivalent of input. If not valid, returns NaN
 * @param input string or number number representation
 */
export function toNumber(input) {
  if (input === null) {
    return NaN;
  }

  if (_.isNumber(input)) {
    return input;
  }

  if (!_.isString(input)) {
    return NaN;
  }

  if (input.trim().length == 0) {
    return NaN;
  }

  const inputWithDecimals = input.replace(',', '.');
  return Number(inputWithDecimals);
}

export function isValidNumber(input) {
  return isNaN(toNumber(input));
}

export function PriceFormatter(additionalOptions) {
  const options = {
    cents: false,
    currency: 'kr',
    currencyPrefixed: false,
    decimals: 2,
    thousand: ' ',
    decimalCharacter: ',',
    ...additionalOptions,
  };

  return (inputNumber) => {
    // Source-ish: http://www.josscrowcroft.com/2011/code/format-unformat-money-currency-javascript/
    const {
      cents,
      currency,
      thousand,
      currencyPrefixed,
      decimals,
      decimalCharacter,
    } = options;
    let amount = cents ? inputNumber / 100 : inputNumber;
    let negative = amount < 0 ? '-' : '';
    let i = `${parseInt(amount = Math.abs(+amount || 0).toFixed(decimals), 10)}`;
    let j = (j = i.length) > 3 ? j % 3 : 0;
    const formattedPrice = negative + (j ? i.substr(0, j) + thousand : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${thousand}`) + (decimals ? decimalCharacter + Math.abs(amount - i).toFixed(decimals).slice(2) : '');

    const retVal = currencyPrefixed ? `${currency} ${formattedPrice}` :
      `${formattedPrice} ${currency}`;
    return retVal.replace(/ /g, '\u00a0');
  };
}

export function percentage(p) {
  if (!p && typeof p !== 'number') {
    return '';
  }

  return `${String(parseFloat(p).toFixed(2)).replace('.', ',')} %`;
}

export function stringToPercentage(string) {
  if (!string || typeof string !== 'string') {
    return 0;
  }
  return parseInt(string.replace(/\D/g, ''));
}

export function percentageToString(cents) {
  const priceFormatter = PriceFormatter({ decimals: 0, currency: '' });
  return priceFormatter(cents).trim();
}

export function moneyToString(cents) {
  const priceFormatter = PriceFormatter({ cents: true, currency: '' });
  return priceFormatter(cents).trim();
}

export function stringToMoney(string) {
  if (!string || typeof string !== 'string') {
    return 0;
  }
  return parseInt(string.replace(/\D/g, ''), 10);
}

export function stringToMoneyNegative(string) {
  if ((!string || typeof string !== 'string')) {
    return 0;
  }
  const sanitizedValue = parseInt(string.replace(/[^0-9-]/g, ''), 10);

  if (string.includes('-')) {
    return sanitizedValue * -1;
  }

  return sanitizedValue;
}

export function stringToFloat(string) {
  return parseFloat(string.replace(',', '.')) || 0;
}

export function floatToString(float, precision = 2) {
  if (!Number.isFinite(float)) {
    return float;
  }
  return float.toFixed(precision).replace('.', ',');
}

export function initials(string) {
  if (string === undefined || string === null || string === '') {
    return string;
  }

  return string.split(' ').map(word => word[0]);
}
